import { type Job, type LibraryCard } from '@shared/domain';
import type { NavigateFunction } from 'react-router-dom';
import {
  addCardToFavoriteSet,
  createFlashcardFromGlossary,
  getFavoriteSets,
  getGlossaryEntries,
  getPocketSops,
  getPinboard,
  getSpotlightRecentSearches,
  recordSpotlightRecentSearch,
  togglePinboardEntry,
  type GlossaryEntry,
  type SopEntry,
} from './knowledgeStore';

export type SpotlightResultType = 'job' | 'card' | 'sop' | 'glossary' | 'setting' | 'incident';

export interface SpotlightResult {
  type: SpotlightResultType;
  id: string;
  title: string;
  subtitle: string;
  matchContext: string;
  score: number;
  whyMatched: string;
  actions: Array<{ id: string; label: string; icon: string }>;
  target: string;
}

export interface SpotlightSuggestion {
  value: string;
  source: 'recent' | 'popular' | 'context';
}

interface SpotlightRecord {
  type: SpotlightResultType;
  id: string;
  title: string;
  subtitle: string;
  body: string;
  tags: string[];
  numericTokens: string[];
  target: string;
}

function normalizeText(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^\w\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function tokenize(query: string): string[] {
  return normalizeText(query)
    .split(' ')
    .filter((token) => token.length > 0);
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function highlightSpotlightText(text: string, query: string): string {
  const tokens = tokenize(query);
  if (tokens.length === 0) return escapeHtml(text);
  let highlighted = escapeHtml(text);
  for (const token of tokens) {
    highlighted = highlighted.replace(new RegExp(`(${escapeRegExp(token)})`, 'gi'), '<mark>$1</mark>');
  }
  return highlighted;
}

function toDigits(value: string): string[] {
  const digits = value.replace(/\D/g, '');
  return digits ? [digits] : [];
}

function buildRecords(jobs: Job[], cards: LibraryCard[]): SpotlightRecord[] {
  const sopRecords: SpotlightRecord[] = getPocketSops().map((entry) => ({
    type: 'sop',
    id: entry.id,
    title: entry.title,
    subtitle: `${entry.category} • ${entry.tags.join(' • ')}`,
    body: entry.steps.join(' '),
    tags: entry.tags,
    numericTokens: [],
    target: `/library?section=sops&sop=${encodeURIComponent(entry.id)}`,
  }));

  const glossaryRecords: SpotlightRecord[] = getGlossaryEntries().map((entry) => ({
    type: 'glossary',
    id: entry.term,
    title: entry.term,
    subtitle: entry.abbreviation ? `${entry.abbreviation} • ${entry.category}` : entry.category,
    body: `${entry.definition} ${entry.nfpaReference || ''} ${entry.relatedTerms.join(' ')}`,
    tags: [entry.category, ...(entry.abbreviation ? [entry.abbreviation] : [])],
    numericTokens: [],
    target: `/library?section=glossary&term=${encodeURIComponent(entry.term)}`,
  }));

  const settingRecords: SpotlightRecord[] = [
    {
      type: 'setting',
      id: 'settings-general',
      title: 'Settings',
      subtitle: 'Device modes, backups, diagnostics',
      body: 'settings device field mode backups privacy diagnostics',
      tags: ['settings', 'backup', 'privacy'],
      numericTokens: [],
      target: '/settings',
    },
  ];

  const jobRecords: SpotlightRecord[] = jobs.map((job) => ({
    type: 'job',
    id: job.id,
    title: job.address || job.customerName || 'Untitled job',
    subtitle: `WO ${job.workOrder || 'N/A'} • ${job.customerName || 'No customer'} • ${job.date}`,
    body: [
      job.address,
      job.customerName,
      job.workOrder,
      job.siteId,
      job.meterNumber,
      job.techNotes,
      job.autoComment,
      job.status,
      job.appointmentType,
    ].join(' '),
    tags: [job.status, job.appointmentType].filter(Boolean),
    numericTokens: [...toDigits(job.workOrder), ...toDigits(job.siteId), ...toDigits(job.meterNumber)],
    target: `/jobs/${job.id}`,
  }));

  const cardRecords: SpotlightRecord[] = cards.map((card) => ({
    type: 'card',
    id: card.id,
    title: card.title,
    subtitle: card.tags.join(' • '),
    body: [card.when, ...card.steps, ...card.fails, ...card.refs].join(' '),
    tags: card.tags,
    numericTokens: [],
    target: `/library?section=cards&card=${encodeURIComponent(card.id)}`,
  }));

  return [...jobRecords, ...cardRecords, ...sopRecords, ...glossaryRecords, ...settingRecords];
}

function prefixAllows(type: SpotlightResultType, prefix?: string): boolean {
  if (!prefix) return true;
  const map: Record<string, SpotlightResultType[]> = {
    wo: ['job'],
    psid: ['job'],
    mtr: ['job'],
    addr: ['job'],
    tag: ['card', 'sop', 'glossary'],
    card: ['card'],
    sop: ['sop'],
    gloss: ['glossary'],
  };
  const allowed = map[prefix];
  return allowed ? allowed.includes(type) : true;
}

function parseQuery(query: string): { mode: 'text' | 'prefix' | 'slash'; value: string; prefix?: string; tokens: string[]; command?: string } {
  const trimmed = query.trim();
  if (!trimmed) return { mode: 'text', value: '', tokens: [] };
  if (trimmed.startsWith('/')) {
    const command = trimmed.slice(1).toLowerCase();
    return { mode: 'slash', value: command, tokens: tokenize(command), command };
  }
  const prefixMatch = trimmed.match(/^([a-z]{2,8}):(.*)$/i);
  if (prefixMatch) {
    const prefix = (prefixMatch[1] ?? '').toLowerCase();
    const value = (prefixMatch[2] ?? '').trim();
    return { mode: 'prefix', value, prefix, tokens: tokenize(value) };
  }
  return { mode: 'text', value: trimmed, tokens: tokenize(trimmed) };
}

function createContext(record: SpotlightRecord, query: string, field: 'title' | 'subtitle' | 'body' | 'tags'): string {
  if (field === 'title') return highlightSpotlightText(record.title, query);
  if (field === 'subtitle') return highlightSpotlightText(record.subtitle, query);
  if (field === 'tags') return `Tags: ${record.tags.map((tag) => highlightSpotlightText(tag, query)).join(', ')}`;
  const source = record.body || record.subtitle || record.title;
  const token = tokenize(query)[0];
  if (!token) return escapeHtml(source);
  const normalized = normalizeText(source);
  const index = normalized.indexOf(token);
  if (index < 0) return highlightSpotlightText(source, query);
  const start = Math.max(0, index - 30);
  const end = Math.min(source.length, index + token.length + 46);
  const prefix = start > 0 ? '…' : '';
  const suffix = end < source.length ? '…' : '';
  return `${prefix}${highlightSpotlightText(source.slice(start, end), query)}${suffix}`;
}

function getActions(type: SpotlightResultType): SpotlightResult['actions'] {
  if (type === 'job') {
    return [
      { id: 'open', label: 'Open', icon: '↗' },
      { id: 'copy-address', label: 'Copy Address', icon: '⧉' },
    ];
  }
  if (type === 'card') {
    return [
      { id: 'open', label: 'Open', icon: '↗' },
      { id: 'pin', label: 'Pin', icon: '📌' },
      { id: 'add-to-set', label: 'Add to Set', icon: '⊕' },
    ];
  }
  if (type === 'sop') {
    return [
      { id: 'open', label: 'Open', icon: '↗' },
      { id: 'start-checklist', label: 'Start Checklist', icon: '☑' },
    ];
  }
  if (type === 'glossary') {
    return [
      { id: 'view', label: 'View', icon: '↗' },
      { id: 'add-to-flashcards', label: 'Add to Flashcards', icon: '🗂' },
    ];
  }
  return [{ id: 'open', label: 'Open', icon: '↗' }];
}

export function searchSpotlight(jobs: Job[], cards: LibraryCard[], query: string, limitPerType = 5): SpotlightResult[] {
  const parsed = parseQuery(query);
  if (parsed.mode === 'slash' || parsed.tokens.length === 0) return [];

  const records = buildRecords(jobs, cards);
  const counts = new Map<SpotlightResultType, number>();

  return records
    .map((record) => {
      if (!prefixAllows(record.type, parsed.prefix)) return null;
      let score = 0;
      let why = '';
      let context = '';
      for (const token of parsed.tokens) {
        const title = normalizeText(record.title);
        const subtitle = normalizeText(record.subtitle);
        const body = normalizeText(record.body);
        const tags = record.tags.map(normalizeText);
        const digits = token.replace(/\D/g, '');

        if (title.startsWith(token)) {
          score += 72;
          why = why || 'Matched: title';
          context = context || createContext(record, parsed.value, 'title');
        } else if (title.includes(token)) {
          score += 54;
          why = why || 'Matched: title';
          context = context || createContext(record, parsed.value, 'title');
        }

        if (subtitle.includes(token)) {
          score += 22;
          why = why || 'Matched: subtitle';
          context = context || createContext(record, parsed.value, 'subtitle');
        }

        if (tags.some((tag) => tag.includes(token))) {
          score += parsed.prefix === 'tag' ? 34 : 20;
          why = why ? `${why}, tags` : 'Matched: tags';
          context = context || createContext(record, parsed.value, 'tags');
        }

        if (body.includes(token)) {
          score += 14;
          why = why || 'Matched: body';
          context = context || createContext(record, parsed.value, 'body');
        }

        if (digits && record.numericTokens.some((value) => value.startsWith(digits))) {
          score += parsed.prefix ? 42 : 26;
          why = why ? `${why}, numeric id` : 'Matched: numeric id';
          context = context || createContext(record, digits, 'subtitle');
        }
      }

      if (score === 0) return null;

      return {
        type: record.type,
        id: record.id,
        title: record.title,
        subtitle: record.subtitle,
        matchContext: context || escapeHtml(record.subtitle),
        score,
        whyMatched: why,
        actions: getActions(record.type),
        target: record.target,
      } satisfies SpotlightResult;
    })
    .filter((result): result is SpotlightResult => Boolean(result))
    .sort((left, right) => right.score - left.score)
    .filter((result) => {
      const used = counts.get(result.type) || 0;
      if (used >= limitPerType) return false;
      counts.set(result.type, used + 1);
      return true;
    });
}

export function getSpotlightSuggestions(jobs: Job[], cards: LibraryCard[], query: string, contextTerms: string[] = []): SpotlightSuggestion[] {
  const normalized = normalizeText(query);
  const suggestions: SpotlightSuggestion[] = [];
  const addSuggestion = (value: string, source: SpotlightSuggestion['source']) => {
    if (!value) return;
    if (suggestions.some((entry) => entry.value.toLowerCase() === value.toLowerCase())) return;
    suggestions.push({ value, source });
  };

  for (const recent of getSpotlightRecentSearches()) {
    if (!normalized || normalizeText(recent).includes(normalized)) {
      addSuggestion(recent, 'recent');
    }
  }

  const popular = new Set<string>();
  for (const job of jobs) {
    if (job.address) popular.add(job.address.split(' ').slice(0, 2).join(' '));
    if (job.workOrder) popular.add(job.workOrder);
  }
  for (const card of cards) {
    card.tags.forEach((tag) => popular.add(tag));
  }
  for (const sop of getPocketSops()) {
    sop.tags.forEach((tag) => popular.add(tag));
  }
  for (const entry of getGlossaryEntries()) {
    popular.add(entry.term);
  }

  for (const value of popular) {
    if (!normalized || normalizeText(value).includes(normalized)) {
      addSuggestion(value, 'popular');
    }
  }

  for (const term of contextTerms) {
    if (!normalized || normalizeText(term).includes(normalized)) {
      addSuggestion(term, 'context');
    }
  }

  return suggestions.slice(0, 8);
}

export function getSlashCommands(query: string): Array<{ id: string; label: string; target: string }> {
  const parsed = parseQuery(query);
  if (parsed.mode !== 'slash') return [];
  const commands = [
    { id: 'new', label: 'New Job', target: '/jobs' },
    { id: 'paste', label: 'Paste Intake', target: '/intake' },
    { id: 'library', label: 'Open Library', target: '/library' },
    { id: 'settings', label: 'Open Settings', target: '/settings' },
  ];
  return commands.filter((command) => command.id.startsWith(parsed.command || ''));
}

export function executeSpotlightAction(
  actionId: string,
  result: SpotlightResult,
  jobs: Job[],
  navigate: NavigateFunction,
): { ok: boolean; message: string } {
  if (actionId === 'open' || actionId === 'view') {
    navigate(result.target);
    return { ok: true, message: 'Opened' };
  }

  if (actionId === 'copy-address' && result.type === 'job') {
    const job = jobs.find((entry) => entry.id === result.id);
    if (!job?.address) return { ok: false, message: 'No address available' };
    void navigator.clipboard?.writeText(job.address);
    return { ok: true, message: 'Address copied' };
  }

  if (actionId === 'pin' && result.type === 'card') {
    const pinned = togglePinboardEntry({
      cardId: result.id,
      kind: 'card',
      title: result.title,
    });
    return { ok: true, message: pinned ? 'Card pinned' : 'Card unpinned' };
  }

  if (actionId === 'add-to-set' && result.type === 'card') {
    const firstSet = getFavoriteSets()[0];
    if (!firstSet) return { ok: false, message: 'Create a favorite set first' };
    addCardToFavoriteSet(firstSet.id, result.id);
    return { ok: true, message: `Added to ${firstSet.name}` };
  }

  if (actionId === 'add-to-flashcards' && result.type === 'glossary') {
    const entry = getGlossaryEntries().find((item) => item.term === result.id);
    if (!entry) return { ok: false, message: 'Glossary entry not found' };
    createFlashcardFromGlossary(entry);
    return { ok: true, message: 'Flashcard created' };
  }

  if (actionId === 'start-checklist' && result.type === 'sop') {
    navigate(result.target);
    return { ok: true, message: 'SOP opened' };
  }

  return { ok: false, message: 'Action unavailable' };
}

export function rememberSpotlightSearch(query: string): void {
  recordSpotlightRecentSearch(query);
}

export function isPinnedCard(cardId: string): boolean {
  return getPinboard().some((entry) => entry.kind === 'card' && entry.cardId === cardId);
}

export function getGlossaryEntry(term: string): GlossaryEntry | null {
  return getGlossaryEntries().find((entry) => entry.term === term) ?? null;
}

export function getSopEntry(id: string): SopEntry | null {
  return getPocketSops().find((entry) => entry.id === id) ?? null;
}
